import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, ArrowUpRight, Building2, Network, Lock } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { fetchMajorCustomers } from "../lib/api";
import { useAuth } from "../context/AuthContext";

function pctColor(p) {
  if (p == null) return "#9a978d";
  if (p >= 30) return "#E24B4A";
  if (p >= 15) return "#BA7517";
  return "#1D9E75";
}

export default function DependsOn() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [locked, setLocked] = useState(false);
  const [q, setQ] = useState("");
  const { login, user } = useAuth();

  useEffect(() => {
    setLoading(true);
    setLocked(false);
    fetchMajorCustomers()
      .then(setData)
      .catch((e) => {
        setData([]);
        if (e?.response?.status === 401) setLocked(true);
      })
      .finally(() => setLoading(false));
  }, [user]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return data;
    return data.filter(
      (b) =>
        b.customer.toLowerCase().includes(s) ||
        b.dependents.some((d) => d.ticker.toLowerCase().includes(s) || d.name.toLowerCase().includes(s))
    );
  }, [data, q]);

  const totalLinks = useMemo(() => data.reduce((a, b) => a + b.count, 0), [data]);

  return (
    <div className="min-h-screen bg-[#fbfaf6]">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#BA7517]">
          <Network className="h-4 w-4" /> Customer concentration map
        </div>
        <h1 className="mt-3 font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-[#1a1a18]">
          Who depends on whom?
        </h1>
        <p className="mt-4 max-w-2xl text-base text-neutral-600">
          The reverse view of customer concentration. We pulled the <em>named</em> major customers out of
          S&amp;P 500 10-K filings — see which giants (Walmart, T-Mobile, the U.S. Government…) hold
          outsized leverage over their suppliers, straight from tagged SEC XBRL.
        </p>

        <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-neutral-500">
          <span><strong className="text-[#1a1a18]">{data.length}</strong> named customers</span>
          <span><strong className="text-[#1a1a18]">{totalLinks}</strong> dependency links</span>
        </div>

        <div className="mt-8 relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
          <input
            data-testid="dependson-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search a customer or a ticker (e.g. Walmart, SBAC)…"
            className="w-full rounded-full border border-[#e3e0d6] bg-white py-2.5 pl-10 pr-4 text-sm outline-none transition-colors focus:border-[#1a1a18]"
          />
        </div>

        {loading ? (
          <div className="mt-16 text-center text-neutral-400" data-testid="dependson-loading">Loading dependency map…</div>
        ) : locked ? (
          <div data-testid="dependson-locked" className="mt-14 rounded-xl border border-[#e8e6df] bg-white px-6 py-16 text-center">
            <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-[#FCEBEB]">
              <Lock className="h-6 w-6 text-[#E24B4A]" />
            </div>
            <h2 className="mt-5 font-heading text-2xl font-semibold text-[#1a1a18]">The Customer Map is free — just sign in</h2>
            <p className="mx-auto mt-3 max-w-md text-neutral-600">
              See which giants — Walmart, T-Mobile, the U.S. Government — hold outsized leverage over
              their S&amp;P 500 suppliers. Create a free account to view it.
            </p>
            <button
              data-testid="dependson-signup-btn"
              onClick={() => login("customer_map_locked")}
              className="mt-6 rounded-full bg-[#E24B4A] px-6 py-3 text-sm font-medium text-white transition-transform active:scale-95 hover:opacity-90"
            >
              Sign up free with Google
            </button>
          </div>
        ) : (
          <div className="mt-10 space-y-4" data-testid="dependson-list">
            {filtered.map((b) => (
              <div
                key={b.customer}
                data-testid={`buyer-${b.customer.replace(/\s+/g, "-").toLowerCase()}`}
                className="rounded-xl border border-[#e8e6df] bg-white p-5 md:p-6 transition-shadow hover:shadow-[0_2px_24px_rgba(26,26,24,0.06)]"
              >
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="grid h-10 w-10 place-items-center rounded-lg bg-[#1a1a18] text-white">
                      <Building2 className="h-5 w-5" />
                    </div>
                    <div>
                      <h2 className="font-heading text-xl font-semibold text-[#1a1a18]">{b.customer}</h2>
                      <p className="text-sm text-neutral-500">
                        named by <strong className="text-[#1a1a18]">{b.count}</strong>{" "}
                        {b.count === 1 ? "company" : "companies"} as a major customer
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  {b.dependents.map((d) => (
                    <Link
                      key={d.ticker}
                      to={`/company/${d.ticker}`}
                      data-testid={`dependent-${d.ticker}`}
                      className="group inline-flex items-center gap-2 rounded-lg border border-[#ece9e0] bg-[#fbfaf6] py-1.5 pl-3 pr-2.5 text-sm transition-colors hover:border-[#1a1a18]"
                      title={`${d.name} — ${d.pct}% of revenue from ${b.customer}`}
                    >
                      <span className="font-semibold text-[#1a1a18]">{d.ticker}</span>
                      <span
                        className="rounded px-1.5 py-0.5 font-mono text-xs font-semibold text-white"
                        style={{ backgroundColor: pctColor(d.pct) }}
                      >
                        {d.pct}%
                      </span>
                      <ArrowUpRight className="h-3.5 w-3.5 text-neutral-400 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </Link>
                  ))}
                </div>
              </div>
            ))}
            {filtered.length === 0 && (
              <div className="py-16 text-center text-neutral-400" data-testid="dependson-empty">
                No named customers match “{q}”.
              </div>
            )}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
